import {
  DAY_CLOSING_STATUS_LABELS,
  SHIFT_LEDGER_STATUS_LABELS,
  type BusinessDayDetail,
  type DayCorrection,
  POSTING_KEYS,
  parsePostingKeyRef,
} from "@station/contracts";
import { Alert, Button, Card, MoneyText, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { FilePlus2 } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../../features/counterparties/shared.js";
import { Section } from "../../../../features/expenses/shared.js";
import { CorrectionDialog } from "../../../../features/shifts/day-end-dialogs.js";
import {
  ChecklistCard,
  DayShiftsPanel,
  DayTotalsPanel,
  DayZReportsPanel,
} from "../../../../features/shifts/day-end-panels.js";
import { DAY_LEDGER_TONE, DAY_STATUS_TONE } from "../../../../features/shifts/day-end-values.js";
import { useBusinessDay, useShiftPermissions } from "../../../../features/shifts/queries.js";
import { Field, ShiftsPage } from "../../../../features/shifts/shared.js";
import { formatMinor } from "../../../../features/shifts/shift-values.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatCount, formatDate, formatDateTime } from "../../../../lib/format.js";

export const Route = createFileRoute("/_app/vardiya/gun-sonu/$id")({
  component: BusinessDayPage,
});

/**
 * KİLİTLİ iş gününün görünümü: kapanışta DONDURULAN kontrol listesi ve toplamlar, özet fiş bağlantısı,
 * POS batch'leri ve düzeltme kayıtları.
 *
 * Kilit AÇILMAZ (ADR 0013 karar 7): bu ekranda "yeniden aç" düğmesi YOKTUR; değişiklik yalnızca ayrı,
 * değiştirilemez bir DÜZELTME KAYDIYLA yapılır (`shifts.day.correct` + step-up).
 */
function BusinessDayPage(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const permissions = useShiftPermissions();
  const day = useBusinessDay(id);
  const title = day.data ? formatDate(day.data.businessDate) : t("menu.shiftDayEnd");

  return (
    <ShiftsPage
      title={title}
      breadcrumb={[
        {
          label: t("menu.shiftDayEnd"),
          link: (children, className) => (
            <Link to="/vardiya/gun-sonu" className={className}>
              {children}
            </Link>
          ),
        },
        { label: title },
      ]}
    >
      {({ companyId }) =>
        !permissions.canRead ? (
          <Alert tone="info">{t("shifts.noPermission")}</Alert>
        ) : day.error ? (
          <Alert tone="danger">{errorMessage(day.error)}</Alert>
        ) : !day.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <BusinessDayDetailView companyId={companyId} day={day.data as BusinessDayDetail} />
        )
      }
    </ShiftsPage>
  );
}

function BusinessDayDetailView({
  companyId,
  day,
}: {
  companyId: string;
  day: BusinessDayDetail;
}): ReactElement {
  const { t } = useTranslation();
  const permissions = useShiftPermissions();
  const [correcting, setCorrecting] = useState(false);

  return (
    <>
      <Card className="grid gap-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-lg font-semibold tabular">{formatDate(day.businessDate)}</span>
          <StatusBadge tone={DAY_STATUS_TONE[day.status]}>
            {DAY_CLOSING_STATUS_LABELS[day.status]}
          </StatusBadge>
          <span className="text-sm text-muted-foreground">{day.stationName}</span>
        </div>
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label={t("shifts.dayEnd.fields.closedAt")}>{formatDateTime(day.closedAt)}</Field>
          <Field label={t("shifts.dayEnd.fields.ledger")}>
            <span className="flex flex-wrap items-center gap-2">
              <StatusBadge tone={DAY_LEDGER_TONE[day.salesLedgerStatus]}>
                {SHIFT_LEDGER_STATUS_LABELS[day.salesLedgerStatus]}
              </StatusBadge>
              {day.salesEntryId ? (
                <Link
                  to="/muhasebe/fisler/$id"
                  params={{ id: day.salesEntryId }}
                  className="underline underline-offset-4"
                >
                  {t("shifts.dayEnd.openEntry")}
                </Link>
              ) : null}
            </span>
          </Field>
          <Field label={t("shifts.dayEnd.fields.posBatches")}>{formatCount(day.posBatchIds.length)}</Field>
          <Field label={t("shifts.dayEnd.fields.corrections")}>{formatCount(day.corrections.length)}</Field>
        </dl>
        {day.salesLedgerStatus === "QUEUED" ? (
          <Alert tone="warning" title={t("shifts.dayEnd.queuedTitle")}>
            {t("shifts.dayEnd.queuedBody")}{" "}
            <Link to="/muhasebe/eslestirmeler" className="underline underline-offset-4">
              {t("shifts.dayEnd.goto.mappings")}
            </Link>
          </Alert>
        ) : null}
        {day.note ? <p className="text-sm text-muted-foreground">{day.note}</p> : null}
      </Card>

      <ChecklistCard
        items={day.checklist.items}
        canClose={day.checklist.canClose}
        stationId={day.stationId}
        businessDate={day.businessDate}
        locked={day.status === "CLOSED"}
      />
      <DayTotalsPanel totals={day.totals} />
      <DayShiftsPanel shifts={day.shifts} />
      <DayZReportsPanel zReports={day.zReports} stationId={day.stationId} businessDate={day.businessDate} />

      <Section
        title={t("shifts.dayEnd.correction.listTitle")}
        actions={
          permissions.canCorrectDay && day.status === "CLOSED" ? (
            <Button variant="outline" size="sm" onClick={() => setCorrecting(true)}>
              <FilePlus2 aria-hidden="true" />
              {t("shifts.dayEnd.correction.title")}
            </Button>
          ) : null
        }
      >
        <p className="text-sm text-muted-foreground">{t("shifts.dayEnd.correction.listHint")}</p>
        <SimpleTable
          caption={t("shifts.dayEnd.correction.caption")}
          head={
            <tr>
              <th scope="col" className={thClass}>
                {t("shifts.dayEnd.correction.no")}
              </th>
              <th scope="col" className={thClass}>
                {t("shifts.dayEnd.correction.reason")}
              </th>
              <th scope="col" className={thClass}>
                {t("shifts.dayEnd.correction.postingDate")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("shifts.records.amount")}
              </th>
              <th scope="col" className={thClass}>
                {t("shifts.dayEnd.fields.ledger")}
              </th>
            </tr>
          }
        >
          {day.corrections.length === 0 ? (
            <tr className="border-t">
              <td className={tdClass} colSpan={5}>
                {t("shifts.dayEnd.correction.empty")}
              </td>
            </tr>
          ) : (
            day.corrections.map((correction) => <CorrectionRow key={correction.id} correction={correction} />)
          )}
        </SimpleTable>
      </Section>

      <CorrectionDialog open={correcting} onOpenChange={setCorrecting} companyId={companyId} day={day} />
    </>
  );
}

function CorrectionRow({ correction }: { correction: DayCorrection }): ReactElement {
  const { t } = useTranslation();
  return (
    <tr className="border-t align-top">
      <td className={`${tdClass} font-mono`}>{correction.correctionNo}</td>
      <td className={tdClass}>
        {correction.reason}
        <ul className="mt-1 grid gap-0.5 text-xs text-muted-foreground">
          {correction.lines.map((line, index) => {
            const parsed = parsePostingKeyRef(line.key);
            return (
              <li key={`${correction.id}-${index}`}>
                {POSTING_KEYS[parsed.key]?.label ?? parsed.key}
                {parsed.param ? ` (${parsed.param})` : ""} ·{" "}
                {t(`shifts.dayEnd.correction.sideLabel.${line.side}`)} · {formatMinor(line.amount)}
              </li>
            );
          })}
        </ul>
      </td>
      <td className={`${tdClass} tabular`}>{formatDate(correction.postingDate)}</td>
      <td className={tdRightClass}>
        <MoneyText value={correction.amount} />
      </td>
      <td className={tdClass}>
        <span className="flex flex-wrap items-center gap-2">
          <StatusBadge tone={DAY_LEDGER_TONE[correction.ledgerStatus]}>
            {SHIFT_LEDGER_STATUS_LABELS[correction.ledgerStatus]}
          </StatusBadge>
          {correction.journalEntryId ? (
            <Link
              to="/muhasebe/fisler/$id"
              params={{ id: correction.journalEntryId }}
              className="underline underline-offset-4"
            >
              {t("shifts.dayEnd.openEntry")}
            </Link>
          ) : null}
        </span>
      </td>
    </tr>
  );
}
